import { useStringFlagValue } from '@openfeature/react-sdk';
import { useMemo } from 'react';
import { z } from 'zod';

import {
  translatorArabicFlag,
  translatorEchoCancellationToggleFlag,
  translatorEvaluationUiFlag,
  translatorFrenchFlag,
  translatorGermanFlag,
  translatorHokkienFlag,
  translatorIndonesianFlag,
  translatorItalianFlag,
  translatorKoreanFlag,
  translatorMonashEvaluationFlag,
  translatorNavFlag,
  translatorPolishFlag,
  translatorPortugueseFlag,
  translatorRomanianFlag,
  translatorRussianFlag,
  translatorSpanishFlag,
  translatorTagalogFlag,
  translatorThaiFlag,
  translatorTurkishFlag,
  translatorUkrainianFlag,
  useTranslatorGatedLanguages,
} from '@heidi/integrations/flags';

// The boolean translator flags are declared and read once in
// `packages/integrations/src/flags.ts` (ruling 36). These constants derive from
// those descriptors; re-declaring a literal here would be the second minting
// site `scripts/check-flag-getter-doors.test.ts` scans for.
export const TRANSLATOR_NAV_FLAG_KEY = translatorNavFlag.key;
export const TRANSLATOR_EVALUATION_UI_FLAG_KEY = translatorEvaluationUiFlag.key;
export const TRANSLATOR_MONASH_EVALUATION_FLAG_KEY = translatorMonashEvaluationFlag.key;
export const TRANSLATOR_ECHO_CANCELLATION_TOGGLE_FLAG_KEY =
  translatorEchoCancellationToggleFlag.key;

export const TRANSLATOR_GATED_LANGUAGE_FLAGS = {
  th: translatorThaiFlag.key,
  nan: translatorHokkienFlag.key,
  ar: translatorArabicFlag.key,
  it: translatorItalianFlag.key,
  ko: translatorKoreanFlag.key,
  fr: translatorFrenchFlag.key,
  de: translatorGermanFlag.key,
  id: translatorIndonesianFlag.key,
  es: translatorSpanishFlag.key,
  tr: translatorTurkishFlag.key,
  pt: translatorPortugueseFlag.key,
  pl: translatorPolishFlag.key,
  uk: translatorUkrainianFlag.key,
  ro: translatorRomanianFlag.key,
  tl: translatorTagalogFlag.key,
  ru: translatorRussianFlag.key,
} as const;

export type TranslatorGatedLanguageCode = keyof typeof TRANSLATOR_GATED_LANGUAGE_FLAGS;

// The two STRING flags stay app-side: no descriptor can hold a string default
// yet, so there is nowhere in the package module to declare the pair.
export const TRANSLATOR_RELEASE_TEST_FLAG_KEY = 'config-translator-release-test';
export const TRANSLATOR_ONBOARDING_FLAG_KEY = 'release-translator-onboarding';

const releaseTestFlagSchema = z.array(z.string().min(1)).catch([]);

export function parseTranslatorReleaseTestFlag(raw: string): Set<string> {
  if (!raw) return new Set();
  try {
    return new Set(releaseTestFlagSchema.parse(JSON.parse(raw)));
  } catch {
    return new Set();
  }
}

export function useTranslatorLanguageGates(): {
  gated: Record<TranslatorGatedLanguageCode, boolean>;
  activeTestVariants: Set<string>;
} {
  const gated = useTranslatorGatedLanguages();
  const releaseTestRaw = useStringFlagValue(TRANSLATOR_RELEASE_TEST_FLAG_KEY, '');
  const activeTestVariants = useMemo(
    () => parseTranslatorReleaseTestFlag(releaseTestRaw),
    [releaseTestRaw]
  );

  return {
    gated,
    activeTestVariants,
  };
}

// FS15: 'complex-onboarding' was removed. It rendered questions with no input
// mechanism, so only skip worked. Re-add here when the variant is functional.
const onboardingVariantSchema = z.enum(['simple-tutorial']);

export type TranslatorOnboardingVariant = z.infer<typeof onboardingVariantSchema>;

export function parseTranslatorOnboardingVariant(
  raw: string
): TranslatorOnboardingVariant | null {
  const parsed = onboardingVariantSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

export function useTranslatorOnboardingVariant(): TranslatorOnboardingVariant | null {
  const raw = useStringFlagValue(TRANSLATOR_ONBOARDING_FLAG_KEY, '');
  return parseTranslatorOnboardingVariant(raw);
}
